import { el } from '../utils/dom.js';

/**
 * Tileset settings dialog — edit name, tile dimensions, margin, and spacing.
 * Changes update the tileset in state and re-render palette + canvas.
 */
export class TilesetSettingsDialog {
  constructor(state, mapCanvas, compositor) {
    this.state = state;
    this.mapCanvas = mapCanvas;
    this.compositor = compositor;
  }

  show(tilesetIndex) {
    const tilesets = this.state.get('tilesets');
    const ts = tilesets[tilesetIndex];
    if (!ts) return;

    const overlay = el('div', { className: 'dialog-overlay', onClick: (e) => {
      if (e.target === overlay) overlay.remove();
    }});
    const dialog = el('div', { className: 'dialog' });

    dialog.appendChild(el('h2', { className: 'dialog-title' }, 'Tileset Settings'));

    // Name
    const nameGroup = el('div', { className: 'form-group' });
    nameGroup.appendChild(el('label', { className: 'form-label' }, 'Name'));
    const nameInput = el('input', { type: 'text', className: 'form-input', value: ts.name });
    nameGroup.appendChild(nameInput);
    dialog.appendChild(nameGroup);

    // Tile dimensions
    const tileRow = el('div', { className: 'form-row' });

    const twField = el('div', { className: 'form-field' });
    twField.append(el('label', { className: 'form-label-sm' }, 'Tile Width'));
    const twInput = el('input', { type: 'number', className: 'form-input', value: String(ts.tileWidth), min: '8', max: '128' });
    twField.appendChild(twInput);
    tileRow.appendChild(twField);

    const thField = el('div', { className: 'form-field' });
    thField.append(el('label', { className: 'form-label-sm' }, 'Tile Height'));
    const thInput = el('input', { type: 'number', className: 'form-input', value: String(ts.tileHeight), min: '8', max: '128' });
    thField.appendChild(thInput);
    tileRow.appendChild(thField);

    dialog.appendChild(tileRow);

    // Margin & Spacing
    const msRow = el('div', { className: 'form-row' });

    const marginField = el('div', { className: 'form-field' });
    marginField.append(el('label', { className: 'form-label-sm' }, 'Margin'));
    const marginInput = el('input', { type: 'number', className: 'form-input', value: String(ts.margin || 0), min: '0', max: '32' });
    marginField.appendChild(marginInput);
    msRow.appendChild(marginField);

    const spacingField = el('div', { className: 'form-field' });
    spacingField.append(el('label', { className: 'form-label-sm' }, 'Spacing'));
    const spacingInput = el('input', { type: 'number', className: 'form-input', value: String(ts.spacing || 0), min: '0', max: '32' });
    spacingField.appendChild(spacingInput);
    msRow.appendChild(spacingField);

    dialog.appendChild(msRow);

    // Info text
    dialog.appendChild(el('div', {
      style: { fontSize: '11px', color: 'var(--text-dim)', marginTop: '8px' },
    }, 'Margin: pixels from image edge to first tile. Spacing: pixels between adjacent tiles.'));

    // Preview of calculated values
    const previewEl = el('div', {
      style: { fontSize: '11px', color: 'var(--text-dim)', marginTop: '4px' },
    });
    const updatePreview = () => {
      const tw = parseInt(twInput.value, 10) || ts.tileWidth;
      const th = parseInt(thInput.value, 10) || ts.tileHeight;
      const m = parseInt(marginInput.value, 10) || 0;
      const s = parseInt(spacingInput.value, 10) || 0;
      const cols = Math.floor((ts.imageWidth - 2 * m + s) / (tw + s));
      const rows = Math.floor((ts.imageHeight - 2 * m + s) / (th + s));
      const count = cols * rows;
      previewEl.textContent = `Image: ${ts.imageWidth}x${ts.imageHeight}px — ${cols} columns, ${rows} rows, ${count} tiles`;
    };
    updatePreview();
    twInput.addEventListener('input', updatePreview);
    thInput.addEventListener('input', updatePreview);
    marginInput.addEventListener('input', updatePreview);
    spacingInput.addEventListener('input', updatePreview);
    dialog.appendChild(previewEl);

    // Buttons
    const btnRow = el('div', { className: 'dialog-buttons' });
    const applyBtn = el('button', { className: 'btn btn-primary', onClick: () => {
      const tw = parseInt(twInput.value, 10) || ts.tileWidth;
      const th = parseInt(thInput.value, 10) || ts.tileHeight;
      const m = parseInt(marginInput.value, 10) || 0;
      const s = parseInt(spacingInput.value, 10) || 0;
      const cols = Math.floor((ts.imageWidth - 2 * m + s) / (tw + s));
      const rows = Math.floor((ts.imageHeight - 2 * m + s) / (th + s));

      ts.name = nameInput.value || ts.name;
      ts.tileWidth = tw;
      ts.tileHeight = th;
      ts.margin = m;
      ts.spacing = s;
      ts.columns = cols;
      ts.tileCount = cols * rows;

      // Notify state changed
      this.state._notify('tilesets', tilesets, tilesets);
      this.mapCanvas.invalidateAll();
      this.compositor.requestRender();
      overlay.remove();
    }}, 'Apply');
    const cancelBtn = el('button', { className: 'btn', onClick: () => overlay.remove() }, 'Cancel');
    btnRow.append(applyBtn, cancelBtn);
    dialog.appendChild(btnRow);

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);
  }
}
